import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { ReportsHeader, usePeriod } from '../components/ReportsHeader.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface ClientRow {
  organizationId: string;
  organizationName: string;
  ticketCount: number;
  totalMinutes: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  avgMinutesPerTicket: number;
}

interface ClientHoursResponse {
  period: { granularity: 'month' | 'quarter'; start: string; end: string; label: string };
  clients: ClientRow[];
}

interface TicketRow {
  ticketId: string;
  displayNumber: number;
  subject: string | null;
  status: string;
  totalMinutes: number;
  billableMinutes: number;
  nonBillableMinutes: number;
}

interface DrilldownState {
  loading: boolean;
  error: string | null;
  tickets: TicketRow[] | null;
}

type SortKey = 'name' | 'tickets' | 'billable' | 'nonBillable' | 'total' | 'avg';

function formatHours(min: number): string {
  return `${(min / 60).toFixed(1)}h`;
}

export function ReportsClientHoursPage(): React.ReactElement {
  useDocumentTitle('Client hours per ticket');
  const { agent } = useAuth();
  const { period } = usePeriod();
  const [data, setData] = useState<ClientHoursResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<Record<string, DrilldownState | undefined>>({});

  const periodQuery = useMemo(
    () =>
      new URLSearchParams({
        granularity: period.granularity,
        periodStart: period.periodStart,
      }).toString(),
    [period.granularity, period.periodStart],
  );

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    setExpanded({});
    apiFetch<ClientHoursResponse>(`/api/reports/client-hours?${periodQuery}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
  }, [periodQuery]);

  function toggleExpand(orgId: string): void {
    const current = expanded[orgId];
    if (current) {
      // Already loaded or loading — collapse.
      setExpanded((prev) => ({ ...prev, [orgId]: undefined }));
      return;
    }
    setExpanded((prev) => ({ ...prev, [orgId]: { loading: true, error: null, tickets: null } }));
    apiFetch<{ tickets: TicketRow[] }>(`/api/reports/client-hours/${orgId}?${periodQuery}`)
      .then((r) => {
        setExpanded((prev) => ({ ...prev, [orgId]: { loading: false, error: null, tickets: r.tickets } }));
      })
      .catch((err) => {
        setExpanded((prev) => ({
          ...prev,
          [orgId]: {
            loading: false,
            error: err instanceof ApiError ? err.message : 'Failed to load tickets.',
            tickets: null,
          },
        }));
      });
  }

  const sortedClients = useMemo(() => {
    if (!data) return [];
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: ClientRow, b: ClientRow): number => {
      switch (sortKey) {
        case 'name':
          return dir * a.organizationName.localeCompare(b.organizationName);
        case 'tickets':
          return dir * (a.ticketCount - b.ticketCount);
        case 'billable':
          return dir * (a.billableMinutes - b.billableMinutes);
        case 'nonBillable':
          return dir * (a.nonBillableMinutes - b.nonBillableMinutes);
        case 'total':
          return dir * (a.totalMinutes - b.totalMinutes);
        case 'avg':
          return dir * (a.avgMinutesPerTicket - b.avgMinutesPerTicket);
      }
    };
    return [...data.clients].sort(cmp);
  }, [data, sortKey, sortDir]);

  if (!agent) return <p>Loading…</p>;

  const headerCell = (key: SortKey, label: string, align: 'left' | 'right' = 'right'): React.ReactElement => (
    <th
      className={
        'cursor-pointer select-none px-3 py-2 ' +
        (align === 'right' ? 'text-right' : '') +
        ' hover:text-[var(--color-t3-blue)]'
      }
      onClick={() => {
        if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else {
          setSortKey(key);
          setSortDir(key === 'name' ? 'asc' : 'desc');
        }
      }}
    >
      {label}
      {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="space-y-6">
      <ReportsHeader
        title="Reports"
        description="Hours logged on each client's tickets for the selected period, split billable vs non-billable. Click a client to see the per-ticket breakdown, or download the full detail as CSV."
      />

      <div className="flex justify-end">
        <a href={`/api/reports/client-hours/export.csv?${periodQuery}`}>
          <Button variant="outline">
            <Download className="mr-1.5 h-4 w-4" /> Download CSV
          </Button>
        </a>
      </div>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {!data ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : sortedClients.length === 0 ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">No ticket time logged in this period.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden !p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="w-8 px-3 py-2" aria-label="Expand" />
                  {headerCell('name', 'Client', 'left')}
                  {headerCell('tickets', 'Tickets')}
                  {headerCell('billable', 'Billable')}
                  {headerCell('nonBillable', 'Non-billable')}
                  {headerCell('total', 'Total')}
                  {headerCell('avg', 'Avg / ticket')}
                  <th className="px-3 py-2 text-right">CSV</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((c) => {
                  const drill = expanded[c.organizationId];
                  const isOpen = drill !== undefined;
                  return (
                    <Fragment key={c.organizationId}>
                      <tr
                        className="cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-surface)]"
                        onClick={() => toggleExpand(c.organizationId)}
                      >
                        <td className="px-3 py-2 text-[var(--color-muted)]">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium">{c.organizationName}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.ticketCount}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatHours(c.billableMinutes)}</td>
                        <td className="px-3 py-2 text-right font-mono text-[var(--color-muted)]">
                          {formatHours(c.nonBillableMinutes)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{formatHours(c.totalMinutes)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatHours(c.avgMinutesPerTicket)}</td>
                        <td className="px-3 py-2 text-right">
                          <a
                            href={`/api/reports/client-hours/export.csv?${periodQuery}&organizationId=${c.organizationId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-[var(--color-t3-blue)] hover:underline"
                            aria-label={`Download CSV for ${c.organizationName}`}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-[var(--color-border)]">
                          <td colSpan={8} className="bg-[var(--color-surface)] px-3 py-3">
                            {drill.loading ? (
                              <p className="text-sm text-[var(--color-muted)]">Loading tickets…</p>
                            ) : drill.error ? (
                              <p className="text-sm text-[var(--color-danger)]">{drill.error}</p>
                            ) : drill.tickets && drill.tickets.length > 0 ? (
                              <table className="w-full text-sm">
                                <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                                  <tr>
                                    <th className="px-3 py-1.5">Ticket</th>
                                    <th className="px-3 py-1.5">Subject</th>
                                    <th className="px-3 py-1.5">Status</th>
                                    <th className="px-3 py-1.5 text-right">Billable</th>
                                    <th className="px-3 py-1.5 text-right">Non-billable</th>
                                    <th className="px-3 py-1.5 text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {drill.tickets.map((t) => (
                                    <tr key={t.ticketId} className="border-t border-[var(--color-border)]">
                                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">T-{t.displayNumber}</td>
                                      <td className="px-3 py-1.5">{t.subject ?? '—'}</td>
                                      <td className="px-3 py-1.5 text-[var(--color-muted)]">{t.status}</td>
                                      <td className="px-3 py-1.5 text-right font-mono">{formatHours(t.billableMinutes)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-[var(--color-muted)]">
                                        {formatHours(t.nonBillableMinutes)}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                                        {formatHours(t.totalMinutes)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-sm text-[var(--color-muted)]">No tickets with time in this period.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-muted)]">
            Hours come from time entries logged against each client's tickets in the selected period (voided entries
            excluded). Billable buckets: block_hours, project, quote; everything else is non-billable. Time not tied to a
            ticket is not included.
          </div>
        </Card>
      )}
    </div>
  );
}
